import * as companyManagementService from "../../services/company";
import * as settings from "../../services/settings";

import { Button, Col, DatePicker, Form, Image, Input, Popconfirm, Row, Space, Table, Upload, notification } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import { FormOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { fileTypeImage } from "../../constant/fileType";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { usePermission } from "../../hooks/usePermission";
import { resolveImageUrl } from "../../utils/imageUrl";

export const CreateCompanyNews = ({ t, detailData }) => {
  const [api, contextHolder] = notification.useNotification();
  const { hasPermission, isCompanyFullAccess } = usePermission();
  const can = (p) => isCompanyFullAccess || hasPermission(p);
  const { accessToken } = useAccessToken();
  const [editingKey, setEditingKey] = useState(null);
  const [tableData, setTableData] = useState(defaultTableData);
  const [isSaving, setIsSaving] = useState(false);

  const [searchForm] = Form.useForm();
  const [newsForm] = Form.useForm();
  const [localNewsFile, setLocalNewsFile] = useState(null);

  const start_date = Form.useWatch("dateFrom", searchForm);
  const end_date = Form.useWatch("dateTo", searchForm);

  const localPreviewUrl = useMemo(() => {
    if (!localNewsFile || !(localNewsFile instanceof File)) return null;
    return URL.createObjectURL(localNewsFile);
  }, [localNewsFile]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const getCompanyNews = useCallback(async () => {
    try {
      setTableData((prev) => ({ ...prev, loading: true }));
      const { start_date, end_date } = searchForm.getFieldsValue();
      const params = {
        company_id: detailData?.id,
        page: tableData.pagination.current,
        limit: tableData.pagination.pageSize,
        start_date: start_date ? dayjs(start_date).format("YYYY-MM-DD") : null,
        end_date: end_date ? dayjs(end_date).format("YYYY-MM-DD") : null,
      };
      const data = await companyManagementService.getCompanyNews(accessToken, params);
      const datasource = data.queryCompanyNews.map((item) => ({
        ...item,
        key: item.id,
        image: {
          image: item.image,
          name: item.image ? item.image.replace(/^uploads\//, "") : "",
          url: resolveImageUrl(item.image),
        },
      }));
      setTableData((prev) => ({ ...prev, loading: false, datasource: datasource, total: data.totalCount }));
    } catch (err) {
      console.error("[CreateCompanyNews] getCompanyNews: ", err);
      setTableData((prev) => ({ ...prev, loading: false }));
      const { message } = settings.getErrorMessage(err);
      api.error({ message: "ERROR.", description: message });
    }
  }, [detailData, accessToken, tableData.pagination, searchForm, api]);

  useEffect(() => {
    getCompanyNews();
  }, [getCompanyNews]);

  const onRefresh = () => {
    searchForm.resetFields();
    newsForm.resetFields();
    setLocalNewsFile(null);
    setEditingKey(null);
    setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
  };

  const onSave = async (rec) => {
    try {
      await newsForm.validateFields();
      const values = newsForm.getFieldsValue();

      const img = localNewsFile ?? values.image;
      const hasNewImageFile = Boolean(img && (img.uid || (typeof File !== "undefined" && img instanceof File)));
      const hasExistingServerImage = Boolean(rec?.image && (rec.image.image || rec.image.url) && rec.key !== "add");

      if (rec.key === "add" && !hasNewImageFile) {
        api.warning({
          message: "แจ้งเตือน",
          description: "กรุณาอัปโหลดรูปภาพ",
        });
        return;
      }
      if (rec.key !== "add" && !hasNewImageFile && !hasExistingServerImage) {
        api.warning({
          message: "แจ้งเตือน",
          description: "กรุณาอัปโหลดรูปภาพ",
        });
        return;
      }

      const formData = new FormData();
      formData.append("company_id", detailData.id);
      formData.append("news_header", values["news_header"]);
      formData.append("news_detail", values["news_detail"]);

      if (hasNewImageFile) {
        formData.append("file", img);
      }
      if (editingKey !== "add") {
        formData.append("old_images", rec.id);
      }

      setIsSaving(true);
      await companyManagementService.createCompanyNews(accessToken, formData);
      api.success({
        message: "Success",
        description: editingKey === "add" ? "Create company news success" : "Update company news success",
      });
      setIsSaving(false);
      onRefresh();
    } catch (err) {
      console.error("[CreateCompanyNews] onSave: ", err);
      const { message } = settings.getErrorMessage(err);
      api.error({ message: "ERROR.", description: message });
      setIsSaving(false);
    }
  };

  const onCreate = () => {
    const newData = { key: "add", link: undefined };
    const newDataSource = [...tableData.datasource];
    newDataSource.unshift(newData);
    setLocalNewsFile(null);
    newsForm.setFieldsValue(newData);
    setTableData((prev) => ({ ...prev, datasource: newDataSource }));
    setEditingKey("add");
  };

  const onCancel = () => {
    if (editingKey === "add") {
      const datasource = [...tableData.datasource];
      datasource.shift();
      setTableData((prev) => ({ ...prev, datasource }));
    }
    setLocalNewsFile(null);
    setEditingKey(null);
    newsForm.resetFields();
  };

  const onEdit = (rec) => {
    setLocalNewsFile(null);
    setEditingKey(rec.key);
    newsForm.setFieldsValue({ ...rec });
  };

  const onUpload = (file) => {
    if (!fileTypeImage.includes(file.type)) {
      api.error({ message: "Invalid file type", description: "Please upload file as format .png, .jpg or .jpeg." });
      return Upload.LIST_IGNORE;
    }
    setLocalNewsFile(file);
    newsForm.setFieldsValue({ image: file });
    return false;
  };

  const onSearch = async () => {
    setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
  };

  const columns = [
    {
      title: t("Company_Create.Create_News.Table.Id"),
      dataIndex: "id",
      width: 60,
    },
    {
      title: t("Company_Create.Create_News.Table.Image"),
      width: 400,
      render: (_, rec) => {
        if (rec.key === editingKey) {
          const src = localPreviewUrl || rec.image?.url;
          return (
            <Space className="w-full" size={8} direction="vertical">
              {src ? <Image src={src} className="max-h-32" alt="" /> : null}
              <span className="mb-2">
                {t("Company_Create.Create_News.Table.Currently")} : {localNewsFile?.name ?? rec.image?.name ?? "-"}
              </span>
              {/* ห้ามห่อ Upload ด้วย Form.Item name="image" — Form จะ inject value/onChange ทำให้เลือกไฟล์ไม่ทำงาน */}
              <Upload beforeUpload={onUpload} showUploadList={false} accept={fileTypeImage.join(",")}>
                <Button size="small">{t("BTN_Choose_File", { ns: "buttons" })}</Button>
              </Upload>
            </Space>
          );
        }
        return <Image src={rec.image?.url} className="max-h-32" />;
      },
    },
    {
      title: t("Company_Create.Create_News.Table.Header"),
      dataIndex: "news_header",
      width: 300,
      render: (txt, rec) => {
        if (rec.key === editingKey) {
          return (
            <Form.Item noStyle name={"news_header"}>
              <Input.TextArea />
            </Form.Item>
          );
        }
        return txt;
      },
    },
    {
      title: t("Company_Create.Create_News.Table.Detail"),
      dataIndex: "news_detail",
      width: 300,
      render: (txt, rec) => {
        if (rec.key === editingKey) {
          return (
            <Form.Item noStyle name={"news_detail"}>
              <Input.TextArea />
            </Form.Item>
          );
        }
        return txt;
      },
    },
    {
      title: t("Company_Create.Create_News.Table.Create"),
      dataIndex: "created_at",
      width: 150,
      render: (_, record) => {
        const created_at = record.created_at != null ? dayjs(record.created_at).format("YYYY-MM-DD hh:mm:ss A") : "-";
        return <p>{created_at}</p>;
      },
    },
    {
      title: t("Company_Create.Create_News.Table.Action"),
      width: 220,
      align: "center",
      render: (_, rec) => {
        if (rec.key === editingKey) {
          return (
            <Space className="m=0 p-0">
              <Button size="small" type="primary" onClick={() => onSave(rec)} loading={isSaving}>
                {t("BTN_Save", { ns: "buttons" })}
              </Button>
              {rec.key !== "add" && (
                <DeleteNewsBtn
                  t={t}
                  record={rec}
                  detailData={detailData}
                  accessToken={accessToken}
                  onRefresh={onRefresh}
                  notiApi={api}
                  disabled={!can(permissions.CompanyNews.DELETE)}
                />
              )}
              <Button size="small" onClick={onCancel}>
                {t("BTN_Cancel", { ns: "buttons" })}
              </Button>
            </Space>
          );
        }
        return (
          <Button
            icon={<FormOutlined style={{ fontSize: 20 }} />}
            type="link"
            onClick={() => onEdit(rec)}
            disabled={editingKey || !can(permissions.CompanyNews.CHANGE)}
          />
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}
      <section>
        <div className="my-4 text-base font-bold">{t("Company_Create.Create_News.Search_Title")}</div>
        <Form form={searchForm}>
          <Row gutter={[24, 24]}>
            <Col span={6}>
              <Form.Item label={t("Company_Create.Create_News.Search_Date_From")} name={"start_date"}>
                <DatePicker
                  format={"YYYY-MM-DD"}
                  allowClear
                  disabledDate={(cur) => (end_date ? cur.isAfter(end_date) : false)}
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={t("Company_Create.Create_News.Search_Date_To")} name={"end_date"}>
                <DatePicker
                  format={"YYYY-MM-DD"}
                  allowClear
                  disabledDate={(cur) => (start_date ? cur.isBefore(start_date) : false)}
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col>
              <Button type="primary" onClick={onSearch}>
                {t("BTN_Search", { ns: "buttons" })}
              </Button>
            </Col>
          </Row>
        </Form>
      </section>
      <section>
        <div className="flex justify-between my-4">
          <span className="text-base font-bold">{t("Company_Create.Create_News.Table_Title")}</span>
          {can(permissions.CompanyNews.ADD) && (
            <Button type="primary" onClick={onCreate} disabled={!!editingKey}>
              {t("BTN_Create", { ns: "buttons" })}
            </Button>
          )}
        </div>
        <Form form={newsForm}>
          <Table
            rowKey={"key"}
            loading={tableData.loading}
            columns={columns}
            dataSource={tableData.datasource}
            pagination={{
              ...tableData.pagination,
              total: tableData.total,
            }}
          />
        </Form>
      </section>
    </>
  );
};

const defaultTableData = {
  loading: false,
  datasource: [],
  pagination: { current: 1, pageSize: 10 },
  total: 0,
};

const DeleteNewsBtn = ({ t, record, accessToken, detailData, onRefresh, notiApi, disabled }) => {
  const [submitting, setSubmitting] = useState(false);

  const onConfirmDelete = async () => {
    try {
      setSubmitting(true);
      const body = { id: record.id, company_id: detailData?.id };
      await companyManagementService.deleteCompanyNews(accessToken, body);
      notiApi.success({
        message: "Success",
        description: "Delete company success",
      });
      onRefresh();
    } catch (err) {
      console.error("[DeleteNewsBtn] onConfirmDelete: ", err);
      // ข้อความ error แสดงจาก handlerResponseErr ใน deleteApi แล้ว
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popconfirm
      title={t("Company_Create.Create_News.Table.Question_Confirm_Delete")}
      okText={t("BTN_OK", { ns: "buttons" })}
      cancelText={t("BTN_Cancel", { ns: "buttons" })}
      okButtonProps={{ loading: submitting }}
      disabled={disabled}
      onConfirm={onConfirmDelete}
      getPopupContainer={() => document.body}
    >
      <Button size="small" danger disabled={disabled}>
        {t("BTN_Delete", { ns: "buttons" })}
      </Button>
    </Popconfirm>
  );
};
